import { Popper } from '@mui/material'
import { styled } from '@mui/material/styles'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import { useCallback } from 'react'
import { Placement } from 'react-overlays/cjs/usePopper'
import { Checkbox } from '../../../../components/inputs/Checkbox'
import { Text } from '../../../../components/typography'
import { colorPalette } from '../../../../style/colorPalette'
import {
  SprintDetail,
  SprintStatus,
  SprintStatusDisplayOrder,
} from '../../../../../lib/functions/sprint'
import DateVO from '../../../../../vo/DateVO'
import { groupByStatus } from '../../../../pages/ProductBacklog/components/SprintAssignmentDialog/types'
import { TeamSprintGroup } from './sprintGroupTypes'
import { useSprintGroupCollapse } from './useSprintGroupCollapse'

const StyledPopper = styled(Popper)({
  zIndex: 1300,
  background: colorPalette.monotone[0],
  boxShadow:
    'rgb(0 0 0 / 20%) 0px 5px 5px -3px, rgb(0 0 0 / 14%) 0px 8px 10px 1px, rgb(0 0 0 / 12%) 0px 3px 14px 2px',
  borderRadius: '4px',
  padding: '10px',
  minWidth: '445px',
  maxHeight: '300px',
  overflowY: 'auto',
})

const TeamHeader = styled('div')({
  display: 'flex',
  alignItems: 'center',
  padding: '4px 8px',
  fontWeight: 'bold',
  fontSize: '0.8rem',
  color: colorPalette.monotone[5],
  backgroundColor: colorPalette.monotone[1],
  borderRadius: '2px',
  marginTop: '4px',
  '&:first-of-type': {
    marginTop: 0,
  },
})

const StatusHeader = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '2px 8px 2px 16px',
  cursor: 'pointer',
  fontSize: '0.75rem',
  color: colorPalette.monotone[4],
  '&:hover': {
    backgroundColor: colorPalette.monotone[1],
  },
})

const SprintRow = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  paddingLeft: '24px',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: colorPalette.monotone[1],
  },
})

const SprintLabel = styled(Text)({
  margin: 'auto 10px',
})

const SprintPeriod = styled('span')({
  marginLeft: 'auto',
  paddingRight: '8px',
  fontSize: '0.7rem',
  color: colorPalette.monotone[3],
  whiteSpace: 'nowrap',
})

const DATE_FORMAT = 'MM/DD'

const formatSprintPeriod = (
  startDate: string | undefined,
  endDate: string | undefined
): string => {
  if (!startDate && !endDate) return ''
  const start = startDate ? new DateVO(startDate).format(DATE_FORMAT) : ''
  const end = endDate ? new DateVO(endDate).format(DATE_FORMAT) : ''
  return `${start} - ${end}`
}

const popperModifiers = (offset?: number) => [
  {
    name: 'offset',
    options: { offset: [offset ? offset : 0, 20] },
  },
  {
    name: 'flip',
    enabled: true,
    options: {
      altBoundary: true,
      rootBoundary: 'document',
      padding: 8,
    },
  },
  {
    name: 'preventOverflow',
    enabled: true,
    options: {
      altAxis: true,
      altBoundary: true,
      tether: true,
      rootBoundary: 'document',
      padding: 8,
    },
  },
]

type SprintGroupedCheckBoxListProps = {
  teamSprintGroups: TeamSprintGroup[]
  value: string[] | undefined
  onChange: (newValue: string[] | undefined) => void
}

export const SprintGroupedCheckBoxList = ({
  teamSprintGroups,
  value,
  onChange,
}: SprintGroupedCheckBoxListProps) => {
  const { isCollapsed, toggle } = useSprintGroupCollapse(
    teamSprintGroups,
    value
  )

  const handleCheckChange = useCallback(
    (sprintUuid: string) => {
      const current = value ?? []
      const next = current.includes(sprintUuid)
        ? current.filter(v => v !== sprintUuid)
        : [...current, sprintUuid]
      onChange(next.length > 0 ? next : undefined)
    },
    [value, onChange]
  )

  return (
    <>
      {teamSprintGroups.map(teamGroup => (
        <TeamSection
          key={teamGroup.teamUuid}
          teamGroup={teamGroup}
          value={value}
          isCollapsed={isCollapsed}
          onToggle={toggle}
          onCheckChange={handleCheckChange}
        />
      ))}
    </>
  )
}

type Props = {
  open: boolean
  anchorEl: Element | undefined
  teamSprintGroups: TeamSprintGroup[]
  value: string[] | undefined
  onChange: (newValue: string[] | undefined) => void
  offset?: number
  placement?: Placement
}

export const SprintGroupedCheckBoxPopper = ({
  open,
  anchorEl,
  teamSprintGroups,
  value,
  onChange,
  offset,
  placement,
}: Props) => (
  <StyledPopper
    open={open}
    anchorEl={anchorEl}
    placement={placement}
    modifiers={popperModifiers(offset)}
    disablePortal={true}
  >
    <SprintGroupedCheckBoxList
      teamSprintGroups={teamSprintGroups}
      value={value}
      onChange={onChange}
    />
  </StyledPopper>
)

type TeamSectionProps = {
  teamGroup: TeamSprintGroup
  value: string[] | undefined
  isCollapsed: (teamUuid: string, status: SprintStatus) => boolean
  onToggle: (teamUuid: string, status: SprintStatus) => void
  onCheckChange: (sprintUuid: string) => void
}

const TeamSection = ({
  teamGroup,
  value,
  isCollapsed,
  onToggle,
  onCheckChange,
}: TeamSectionProps) => {
  const { groups: statusGroups } = groupByStatus(teamGroup.sprints)

  return (
    <div>
      <TeamHeader>{teamGroup.teamName}</TeamHeader>
      {SprintStatusDisplayOrder.filter(status => statusGroups.has(status)).map(
        status => (
          <StatusSection
            key={status}
            teamUuid={teamGroup.teamUuid}
            status={status}
            sprints={statusGroups.get(status)!}
            value={value}
            isCollapsed={isCollapsed(teamGroup.teamUuid, status)}
            onToggle={onToggle}
            onCheckChange={onCheckChange}
          />
        )
      )}
    </div>
  )
}

type StatusSectionProps = {
  teamUuid: string
  status: SprintStatus
  sprints: SprintDetail[]
  value: string[] | undefined
  isCollapsed: boolean
  onToggle: (teamUuid: string, status: SprintStatus) => void
  onCheckChange: (sprintUuid: string) => void
}

const StatusSection = ({
  teamUuid,
  status,
  sprints,
  value,
  isCollapsed,
  onToggle,
  onCheckChange,
}: StatusSectionProps) => (
  <>
    <StatusHeader onClick={() => onToggle(teamUuid, status)}>
      <span>
        {status}({sprints.length})
      </span>
      {isCollapsed ? (
        <ExpandMore sx={{ fontSize: 16 }} />
      ) : (
        <ExpandLess sx={{ fontSize: 16 }} />
      )}
    </StatusHeader>
    {!isCollapsed &&
      sprints.map(sprint => (
        <SprintRow key={sprint.uuid} onClick={() => onCheckChange(sprint.uuid)}>
          <Checkbox
            sx={{ marginRight: '8px' }}
            value={sprint.uuid}
            checked={(value ?? []).includes(sprint.uuid)}
            onChange={() => onCheckChange(sprint.uuid)}
            onClick={e => e.stopPropagation()}
          />
          <SprintLabel color={colorPalette.monotone[4]}>
            {sprint.name}
          </SprintLabel>
          <SprintPeriod>
            {formatSprintPeriod(sprint.startDate, sprint.endDate)}
          </SprintPeriod>
        </SprintRow>
      ))}
  </>
)
